import api from "../services/authService";

/**
 * Categorization gold-standard + negative-example REST client.
 *
 * Consumes the BE `feat/categorization-mgmt-api` (PR #512) surface under
 * `/api/pipeline/categorization/`. All calls route through the shared
 * `authService` axios instance so JWT + 401 refresh stay consistent.
 *
 * BE shape (paginated default):
 *   { count, next, previous, results: [...] }
 *
 * Legacy grouped-by-category shape stays reachable behind `?grouped=1`
 * but the new affordances (Settings page split-button, right-click
 * report, badge bulk-fetch) all read the paginated default.
 *
 * Errors:
 *   - 409 Conflict on POST gold-standards or PATCH gold-standards when
 *     the (document, category) uniqueness invariant is violated.
 *   - Server returns `{ detail: "..." }` on most 4xx errors; callers
 *     surface that string in toasts.
 */

const BASE = "/pipeline/categorization";

const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      // BE accepts comma-separated `document_id__in` per PR #512.
      usp.set(key, value.join(","));
      return;
    }
    usp.set(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/**
 * GET the canonical category list. Unauthenticated per PR #512 — the BE
 * intentionally serves this without a JWT so login-adjacent surfaces
 * can render dropdowns before auth completes. We still send the auth
 * header (axios default) but the server ignores it.
 *
 * Returns the raw `{ categories: [...] }` envelope so callers can swap
 * to a future shape (e.g. `{ categories: [...], groups: [...] }`)
 * without re-plumbing the service layer.
 */
export const fetchCategories = async () => {
  const response = await api.get(`${BASE}/categories/`);
  return response.data;
};

/**
 * GET paginated gold-standards. Filters supported (PR #512):
 *   - `category` — exact match
 *   - `search` — icontains on document name + short_summary + notes
 *   - `documentIds` — array → comma-separated `document_id__in`
 *   - `documentId` — single doc filter (`document_id`)
 *   - `page`, `pageSize` — default 50/page, max 200
 *   - `grouped` — pass `1` to hit the legacy grouped-by-category shape
 */
export const listGoldStandards = async ({
  category,
  subtype,
  search,
  documentIds,
  documentId,
  page,
  pageSize,
  grouped,
} = {}) => {
  const params = {
    category,
    // `none` is a real filter value, not a missing one: it selects rows
    // that carry no subtype, which is how a curator finds the examples
    // still needing a label.
    subtype,
    search,
    document_id__in: documentIds,
    document_id: documentId,
    page,
    page_size: pageSize,
    grouped: grouped ? 1 : undefined,
  };
  const response = await api.get(`${BASE}/gold-standards/${buildQuery(params)}`);
  return response.data;
};

/**
 * POST a new gold-standard row from an existing DMS document. The BE
 * reads the PDF, short_summary, and stored doc_category from the
 * `Document` row itself — callers don't re-upload.
 *
 * Returns the created row. Raises on 409 when the (document, category)
 * pair already exists (handled in slice via `rejectWithValue`).
 */
export const createGoldStandardFromDocument = async ({
  documentId,
  category,
  subtype,
  notes,
}) => {
  const payload = { document_id: documentId, category };
  // One row teaches both labels: a Board decision is an Agency Decisions
  // example AND a bva_decision example, so the curator tags it once.
  if (subtype) payload.subtype = subtype;
  if (notes != null) payload.notes = notes;
  const response = await api.post(`${BASE}/gold-standards/`, payload);
  return response.data;
};

export const getGoldStandard = async (id) => {
  const response = await api.get(`${BASE}/gold-standards/${id}/`);
  return response.data;
};

export const patchGoldStandard = async (id, data) => {
  const response = await api.patch(`${BASE}/gold-standards/${id}/`, data);
  return response.data;
};

export const deleteGoldStandard = async (id) => {
  await api.delete(`${BASE}/gold-standards/${id}/`);
};

/**
 * GET paginated negative examples. Same filter shape as listGoldStandards.
 */
export const listNegativeExamples = async ({
  category,
  subtype,
  search,
  documentIds,
  documentId,
  page,
  pageSize,
  grouped,
} = {}) => {
  const params = {
    category,
    subtype,
    search,
    document_id__in: documentIds,
    document_id: documentId,
    page,
    page_size: pageSize,
    grouped: grouped ? 1 : undefined,
  };
  const response = await api.get(`${BASE}/negative-examples/${buildQuery(params)}`);
  return response.data;
};

/**
 * POST a negative example. When `alsoAddGoldStandardFor` is supplied,
 * the BE atomically creates BOTH the NegativeExample row AND a paired
 * GoldStandard row for the corrected category in a single transaction
 * (PR #512). Same-category and unknown-category pairings fail with
 * 400 before any write — this is the contract callers depend on for
 * the "Report incorrect categorization" UX.
 *
 * Response shape when paired:
 *   { negative_example: {...}, gold_standard: {...} }
 * Response shape when unpaired:
 *   The negative-example row directly.
 */
export const createNegativeExample = async ({
  documentId,
  negativeForCategory,
  negativeForSubtype,
  actualSubtype,
  reason,
  alsoAddGoldStandardFor,
  notes,
}) => {
  const payload = {
    document_id: documentId,
    negative_for_category: negativeForCategory,
    reason: reason || "",
  };
  // The confusion worth teaching often lives only at subtype level: a
  // rating decision and a Board decision are both Agency Decisions, so
  // a category-level negative says nothing about the pair.
  if (negativeForSubtype) payload.negative_for_subtype = negativeForSubtype;
  if (actualSubtype) payload.actual_subtype = actualSubtype;
  if (alsoAddGoldStandardFor) {
    payload.also_add_gold_standard_for = alsoAddGoldStandardFor;
  }
  if (notes != null) payload.notes = notes;
  const response = await api.post(`${BASE}/negative-examples/`, payload);
  return response.data;
};

export const getNegativeExample = async (id) => {
  const response = await api.get(`${BASE}/negative-examples/${id}/`);
  return response.data;
};

export const patchNegativeExample = async (id, data) => {
  const response = await api.patch(`${BASE}/negative-examples/${id}/`, data);
  return response.data;
};

export const deleteNegativeExample = async (id) => {
  await api.delete(`${BASE}/negative-examples/${id}/`);
};

/**
 * POST /api/documents/<documentId>/recategorize/
 * Moves the document to a new category and re-organises it in the
 * matter folder tree.
 *
 * `addNegative` (default false) is the user's explicit opt-in to also record
 * the correction as a NegativeExample for the document's previous category, so
 * the categorization model learns from it. `reason` is the optional "why was
 * the AI wrong" note that only matters when `addNegative` is true. When the
 * opt-in is off we send the minimal `{ category }` body so the plain move path
 * is unchanged. The response includes `negative_example_created` so the caller
 * can confirm accurately.
 */
export const recategorizeDocument = async ({
  documentId,
  category,
  addNegative = false,
  reason = "",
}) => {
  const payload = { category };
  if (addNegative) {
    payload.add_negative = true;
    if (reason) payload.reason = reason;
  }
  const response = await api.post(
    `/documents/${documentId}/recategorize/`,
    payload
  );
  return response.data;
};

/**
 * Helper: bulk-fetch the gold-standard + negative-example coverage for a
 * set of visible document IDs. Used by the matter document table to
 * render per-row badges in one pair of calls instead of N×2 lookups.
 *
 * Returns `{ goldStandards: [...], negativeExamples: [...] }` — flat
 * arrays of the result rows from each paginated response (first page
 * only, capped at the BE max of 200 per page; a matter with >200
 * library rows is a non-goal for v1).
 */
export const fetchCategorizationForDocuments = async (documentIds = []) => {
  // Drop null/undefined BEFORE String() — `String(null) === "null"` (truthy),
  // which would otherwise send a literal `null` token to the server.
  const ids = Array.from(
    new Set(
      documentIds
        .filter((id) => id !== null && id !== undefined && id !== "")
        .map(String)
        .filter(Boolean)
    )
  );
  if (ids.length === 0) {
    return { goldStandards: [], negativeExamples: [] };
  }
  const [goldRes, negRes] = await Promise.all([
    listGoldStandards({ documentIds: ids, pageSize: 200 }),
    listNegativeExamples({ documentIds: ids, pageSize: 200 }),
  ]);
  return {
    goldStandards: goldRes?.results || [],
    negativeExamples: negRes?.results || [],
  };
};

/**
 * Extract the document id from a gold-standard or negative-example row.
 * BE PR #512 serializes the linked document as a nested object
 * (`document: { id, name, matter_id }`) when present, but historical
 * grouped-shape rows carry `document_id` directly. Helper centralizes
 * the lookup so callers don't have to branch.
 */
export const getRowDocumentId = (row) => {
  if (!row || typeof row !== "object") return null;
  if (row.document && row.document.id != null) return String(row.document.id);
  if (row.document_id != null) return String(row.document_id);
  return null;
};
